import { Switch } from 'antd';
import Barchart from 'components/molecules/Barchart';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { formatNumWithKMBAlt, getConfig, scaleMaxValue, scaleMinValue } from 'utils/helperMethods';
import { memo, useContext, useMemo, useRef } from 'react';
import { calculateContainerHeight } from 'utils/helperMethods';
import WaterFallChartIndividualised from 'components/molecules/WaterFallChartIndividualised';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import useGrowthMarginBridgesData from './hooks/useGrowthMarginBridgesData';
import Loader from 'components/molecules/Loader';
import { isEmpty } from 'lodash';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { useGrowthMarginBridgesContext } from './GrowthMarginBridgesContext';
import { FilterContextData } from 'contexts/FilterContext';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { UserContext } from 'contexts/ConfigContext';

const MemoisedWaterFallChartIndividualised = memo(
    ({
        _datum,
        chartHeight,
        currentGraphWidth,
        minBound,
        maxBound,
        defaultCurrencySymbol,
        setSelectedBridgesKPI,
        kpiLabelMapping,
        isPercentageUnitSelected,
    }: {
        _datum: any;
        chartHeight: number;
        currentGraphWidth: number;
        minBound: number;
        maxBound: number;
        defaultCurrencySymbol: string;
        setSelectedBridgesKPI: (kpi: string) => void;
        kpiLabelMapping: any;
        isPercentageUnitSelected: boolean;
    }) => {
        return (
            <div
                className="hover:bg-black/5 z-10"
                key={`growth-margin-bridges-waterfall-chart-${_datum.kpiKey}`}
                onClick={() => {
                    setSelectedBridgesKPI(_datum.kpiKey);
                }}
            >
                <WaterFallChartIndividualised
                    containerId={`growth-margin-bridges-waterfall-chart-${_datum.kpiKey}`}
                    key={`growth-margin-bridges-waterfall-chart-${_datum.kpiKey}`}
                    chartHeight={chartHeight}
                    xAxisField="kpiName"
                    yAxisField="kpiValue"
                    colorField="trend"
                    minBound={minBound}
                    maxBound={scaleMaxValue(20, maxBound)}
                    dataSet={[_datum]}
                    chartWidth={currentGraphWidth}
                    toolTipLabel={`${kpiLabelMapping['profit']} Growth`}
                    tooltipFormatter={(xAxis, val) => {
                        return {
                            name: `${kpiLabelMapping['profit']} Growth`,
                            value: isPercentageUnitSelected
                                ? `${val.toFixed(2)}%`
                                : `${defaultCurrencySymbol}${formatNumWithKMBAlt(val, 2, 2)}`,
                        };
                    }}
                    intervalFormatter={(val) => {
                        return isPercentageUnitSelected
                            ? `${val.toFixed(2)}%`
                            : `${defaultCurrencySymbol}${formatNumWithKMBAlt(val, 2, 2)}`;
                    }}
                />
                <p className="text-center">{_datum.kpiName}</p>
            </div>
        );
    },
);

const GrowthBridgesChart = () => {
    const chartRef = useRef<any>(null);
    const chartHeight = calculateContainerHeight(2.3);
    const containerWidth = useContainerWidth({
        containerId: 'growth-margin-bridges-waterfall-chart-infinite-scroll',
        buffer: 300,
    });

    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { defaultCurrencySymbol, kpiLabelMapping } = profitPoolConfig;

    const { singleSelectedTier, profitPoolTierList } = useContext(FilterContextData);
    const selectedTierLabel = useMemo(() => {
        return profitPoolTierList.find((tier) => tier.tierReference === singleSelectedTier[0])?.tierName;
    }, [singleSelectedTier, profitPoolTierList]);
    const { setSelectedBridgesKPI, isPercentageUnitSelected, setIsPercentageUnitSelected } =
        useGrowthMarginBridgesContext();

    const {
        waterFallChartData: profitGrowthBridgesData,
        currentYearData,
        previousYearData,
        isPending,
        isError,
        isSuccess,
        min: waterfallMin,
        max: waterfallMax,
        excelExportData,
    } = useGrowthMarginBridgesData();

    const minGraphWidth = 100;
    const currentDataLength = profitGrowthBridgesData?.length ?? 0;
    const currentContainerWidth = containerWidth;

    const minBound = useMemo(() => {
        return waterfallMin > 0 ? 0 : scaleMinValue(10, waterfallMin);
    }, [waterfallMin]);
    const maxBound = useMemo(() => {
        return waterfallMax > 0 ? waterfallMax : 0;
    }, [waterfallMax]);

    const currentGraphWidth =
        currentContainerWidth / currentDataLength < minGraphWidth
            ? minGraphWidth
            : currentContainerWidth / currentDataLength;

    if (isPending) {
        return (
            <div>
                <p className="text-lg font-semibold">{`${selectedTierLabel || ''} ${
                    kpiLabelMapping['profit']
                } Growth`}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                <p className="text-lg font-semibold">{`${selectedTierLabel || ''} ${
                    kpiLabelMapping['profit']
                } Growth`}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox />
                </div>
            </div>
        );
    }

    if (!isPending && isEmpty(profitGrowthBridgesData)) {
        return (
            <div>
                <p className="text-lg font-semibold">{`${selectedTierLabel || ''} ${
                    kpiLabelMapping['profit']
                } Growth`}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox msg={'No Data'} />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-row items-center justify-between">
                <p className="text-lg font-semibold">{`${selectedTierLabel} ${kpiLabelMapping['profit']} Growth`}</p>
                <div className="flex flex-row gap-x-2 items-center">
                    <div className="flex flex-row gap-1 items-center">
                        <p>{'Abs'}</p>
                        <Switch
                            checked={isPercentageUnitSelected}
                            onChange={() => {
                                setIsPercentageUnitSelected(!isPercentageUnitSelected);
                            }}
                        />
                        <p>{'%'}</p>
                    </div>
                    <div className="flex justify-end items-center">
                        <span className="flex gap-2">
                            <CopyToClipBoard refOfComponent={chartRef} />
                        </span>
                    </div>
                    <div>
                        <DownloadExcel
                            title={`${selectedTierLabel || ''} Profit Growth`}
                            excelData={excelExportData.data}
                            headers={excelExportData.headers}
                        />
                    </div>
                </div>
            </div>
            {/* Legend */}
            <div className="items-center flex flex-row justify-between my-2 mb-4">
                <>
                    <div className="text-sm">
                        <p>{'Change'}</p>
                    </div>
                    <div className="flex flex-row gap-2 text-xs">
                        <div className="bg-[#4D388C] w-3 h-2 self-center" />
                        <p>{'Current and Last Year'}</p>
                        <div className="bg-[#42A531] w-3 h-2 self-center" />
                        <p>{'Positive'}</p>
                        <div className="bg-[#CA3D3D] w-3 h-2 self-center" />
                        <p>{'Negative'}</p>
                    </div>
                    <div />
                </>
            </div>

            {/* Chart */}
            <div className="flex mt-2 flex-row" ref={chartRef}>
                {/* Y Axis */}
                <div className="flex">
                    <Barchart
                        padding={[0, 0, -2, 0]}
                        barWidth={40}
                        xAxisConfig={true}
                        xAxisLabelFormatter={(val) => {
                            return val;
                        }}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`growth-margin-bridges-y-axis`}
                        fields={['brand_name', 'totalProfit', 'type']}
                        minBound={minBound}
                        maxBound={scaleMaxValue(20, maxBound)}
                        width={50}
                        colorKey={'type'}
                        YGrid={false}
                        yAxisConfig={true}
                        yAxisLabelFormatter={(val: any) => {
                            return isPercentageUnitSelected
                                ? `${val}%`
                                : `${defaultCurrencySymbol}${formatNumWithKMBAlt(val)}`;
                        }}
                    />
                </div>
                {/* Last Year Chart */}
                <div className="flex flex-col">
                    <Barchart
                        padding={[0, 0, -8, 0]}
                        barWidth={40}
                        xAxisConfig={true}
                        xAxisLabelFormatter={(val) => {
                            return '';
                        }}
                        dataset={[
                            {
                                brand_name: 'Year Ago',
                                totalProfit: previousYearData,
                                type: '#5F49BC',
                            },
                        ]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`growth-margin-bridges-last-year-chart`}
                        fields={['brand_name', 'totalProfit', 'type']}
                        minBound={minBound}
                        maxBound={scaleMaxValue(20, maxBound)}
                        width={100}
                        colorKey={'type'}
                        yAxisConfig={true}
                        yAxisLabelFormatter={(val: any) => {
                            return '';
                        }}
                        tooltipFormatter={(xAxis, val) => {
                            return {
                                name: `${kpiLabelMapping['profit']} Growth`,
                                value: isPercentageUnitSelected
                                    ? `${val?.toFixed(2)}%`
                                    : `${defaultCurrencySymbol}${formatNumWithKMBAlt(val, 2, 2)}`,
                            };
                        }}
                        intervalFormatter={(val) => {
                            return isPercentageUnitSelected
                                ? `${val?.toFixed(2)}%`
                                : `${defaultCurrencySymbol}${formatNumWithKMBAlt(val, 2, 2)}`;
                        }}
                    />
                    <p className="text-center">{'LY'}</p>
                </div>

                {/* WaterFall Chart */}
                <div
                    id="growth-margin-bridges-waterfall-chart-infinite-scroll"
                    className="flex-1 flex overflow-x-auto max-w-full"
                >
                    {profitGrowthBridgesData.map((_datum) => {
                        return (
                            <MemoisedWaterFallChartIndividualised
                                defaultCurrencySymbol={defaultCurrencySymbol}
                                _datum={_datum}
                                chartHeight={chartHeight}
                                currentGraphWidth={currentGraphWidth}
                                minBound={minBound}
                                maxBound={maxBound}
                                setSelectedBridgesKPI={setSelectedBridgesKPI}
                                kpiLabelMapping={kpiLabelMapping}
                                isPercentageUnitSelected={isPercentageUnitSelected}
                            />
                        );
                    })}
                </div>

                {/* Current Total Chart */}
                <div className="flex flex-col">
                    <Barchart
                        padding={[0, 0, -8, 0]}
                        barWidth={40}
                        xAxisConfig={true}
                        xAxisLabelFormatter={(val) => {
                            return '';
                        }}
                        dataset={[
                            {
                                brand_name: 'Current Year',
                                totalProfit: currentYearData,
                                type: '#5F49BF',
                            },
                        ]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`growth-margin-bridges-current-year-chart`}
                        fields={['brand_name', 'totalProfit', 'type']}
                        minBound={minBound}
                        maxBound={scaleMaxValue(20, maxBound)}
                        width={100}
                        colorKey={'type'}
                        YGrid={true}
                        yAxisConfig={true}
                        tooltipFormatter={(xAxis, val) => {
                            return {
                                name: `${kpiLabelMapping['profit']} Growth`,
                                value: isPercentageUnitSelected
                                    ? `${val.toFixed(2)}%`
                                    : `${defaultCurrencySymbol}${formatNumWithKMBAlt(val, 2, 2)}`,
                            };
                        }}
                        intervalFormatter={(val) => {
                            return isPercentageUnitSelected
                                ? `${val.toFixed(2)}%`
                                : `${defaultCurrencySymbol}${formatNumWithKMBAlt(val, 2, 2)}`;
                        }}
                    />
                    <p className="text-center">{'CY'}</p>
                </div>
            </div>
        </>
    );
};

export default memo(GrowthBridgesChart);
